// Sentry must be first.
import * as Sentry from "@sentry/react";

import { buildTracingUserIdForInterviewApp } from "@repo/app-types/api/tracing";
import type { ContactId } from "@repo/app-types/entities/contact";
import escapeRegExp from "lodash-es/escapeRegExp.js";
import { selectInterview } from "../features/transcriptFragments/transcriptFragmentsSelectors.js";
import type { RootState } from "./store.js";

export interface SentryManager {
  onStoreChange: (getState: () => RootState) => void;
}

const nullSentryManager: SentryManager = {
  onStoreChange: () => {},
};

export function startSentry(): SentryManager {
  // TODO: Use Posthog <> Sentry integration for error reporting.
  Sentry.init({
    // Unset this environment variable in development unless you're testing Sentry
    dsn: import.meta.env.VITE_SENTRY_INTERVIEW_DSN,
    // TODO: Set up component tracing and Redux integrations.
    integrations: [Sentry.browserTracingIntegration()],
    environment: import.meta.env.PROD ? "production" : "development",
    // Trace 2% of sessions. The api inherits this decision for the requests
    // a session makes, so it sets the api's sampling for this app's traffic too.
    tracesSampleRate: 0.02,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      /^\//,
      new RegExp(`^${escapeRegExp(import.meta.env.VITE_API_BASE_URL)}`, "i"),
    ],
  });

  const onContactIdChange = (contactId: ContactId | undefined) => {
    Sentry.setUser(
      contactId
        ? {
            id: buildTracingUserIdForInterviewApp({ contactId }),
          }
        : null,
    );
  };

  const onInterviewIdChange = (interviewId: string | undefined) => {
    Sentry.setTag("interview_id", interviewId);
  };

  let contactId: ContactId | undefined = undefined;
  let interviewId: string | undefined = undefined;

  return {
    onStoreChange: (getState) => {
      const state = getState();
      const interview = selectInterview(state);

      const newContactId = interview?.contact?.id;
      if (newContactId !== contactId) {
        contactId = newContactId;
        onContactIdChange(contactId);
      }

      const newInterviewId = interview?.id;
      if (newInterviewId !== interviewId) {
        interviewId = newInterviewId;
        onInterviewIdChange(interviewId);
      }
    },
  };
}
